// Searchable coin picker — shadcn <Command> inside a <Popover>. Visual match for
// bb-traderx's strategy-wizard asset picker: `h-11 px-4 rounded-xl border-2`,
// border switches to primary when open.

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

const HL_PERPS: { symbol: string; name: string }[] = [
  { symbol: "BTC", name: "Bitcoin" },
  { symbol: "ETH", name: "Ethereum" },
  { symbol: "SOL", name: "Solana" },
  { symbol: "HYPE", name: "Hyperliquid" },
  { symbol: "DOGE", name: "Dogecoin" },
  { symbol: "XRP", name: "Ripple" },
  { symbol: "BNB", name: "BNB" },
  { symbol: "AVAX", name: "Avalanche" },
  { symbol: "ARB", name: "Arbitrum" },
  { symbol: "OP", name: "Optimism" },
  { symbol: "SUI", name: "Sui" },
  { symbol: "APT", name: "Aptos" },
  { symbol: "PEPE", name: "Pepe" },
  { symbol: "WIF", name: "dogwifhat" },
  { symbol: "SEI", name: "Sei" },
  { symbol: "INJ", name: "Injective" },
  { symbol: "LINK", name: "Chainlink" },
  { symbol: "TIA", name: "Celestia" },
  { symbol: "FET", name: "Fetch.ai" },
  { symbol: "NEAR", name: "Near" },
  { symbol: "TON", name: "Toncoin" },
  { symbol: "LDO", name: "Lido" },
  { symbol: "MKR", name: "Maker" },
  { symbol: "ATOM", name: "Cosmos" },
  { symbol: "ADA", name: "Cardano" },
  { symbol: "MATIC", name: "Polygon" },
  { symbol: "DOT", name: "Polkadot" },
  { symbol: "ENA", name: "Ethena" },
  { symbol: "JUP", name: "Jupiter" },
  { symbol: "ORDI", name: "Ordinals" },
];

interface Props {
  value: string;
  onChange: (coin: string) => void;
}

export function CoinPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const selected = HL_PERPS.find((c) => c.symbol === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-11 px-4 text-sm rounded-xl border-2 bg-card-elevated font-mono",
            open ? "border-primary" : "border-border",
            !value && "text-muted-foreground",
          )}
        >
          {value ? (
            <span className="flex items-baseline gap-2">
              <span className="font-bold">{value}</span>
              {selected && <span className="text-xs text-muted-foreground">{selected.name}</span>}
            </span>
          ) : (
            <span>Select an asset…</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search symbol… (BTC, ETH, HYPE)" />
          <CommandList>
            <CommandEmpty>
              <div className="text-xs text-muted-foreground py-1">
                Press Enter to use as a custom symbol
              </div>
            </CommandEmpty>
            <CommandGroup heading="Hyperliquid perps">
              {HL_PERPS.map((c) => (
                <CommandItem
                  key={c.symbol}
                  value={`${c.symbol} ${c.name}`}
                  onSelect={() => {
                    onChange(c.symbol);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 size-4", value === c.symbol ? "opacity-100 text-primary" : "opacity-0")}
                  />
                  <span className="font-mono font-bold w-16">{c.symbol}</span>
                  <span className="text-xs text-muted-foreground">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
